/**
 * Complaints register — Collections → Complaints / Legal → Complaints.
 * Log → acknowledge (deadline capped by Portal Settings) → respond.
 * Named exports (LogComplaintDialog, ComplaintDetailDialog, CaseComplaintsPanel)
 * are reused by the customer file's Complaints tab.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, Plus, Megaphone, Mail, X, Send, CheckCircle2, Clock, Trash2,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  listComplaints, complaintsForCase, complaintCaseSearch, createComplaint,
  updateComplaint, deleteComplaint, addComplaintComm, sendComplaintAck,
  getComplaintsSettings, getAgeingHandlers,
} from "@/lib/api";

const ukDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "—";
};
const todayIso = () => new Date().toISOString().slice(0, 10);
const daysUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00");
  return Math.ceil((d - new Date(new Date().toDateString())) / 86400000);
};

const STATUS_META = {
  open:         { label: "Open",         cls: "bg-rose-100 text-rose-800 border-rose-300" },
  acknowledged: { label: "Acknowledged", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  responded:    { label: "Responded",    cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  closed:       { label: "Closed",       cls: "bg-slate-100 text-slate-700 border-slate-300" },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.open;
  return <Badge variant="outline" className={`text-[10px] ${m.cls}`} data-testid={`complaint-status-${status}`}>{m.label}</Badge>;
}

function DueBadge({ complaint }) {
  if (["responded", "closed"].includes(complaint.status)) return <span className="text-xs text-slate-500">{ukDate(complaint.final_response_due)}</span>;
  if (!complaint.final_response_due) return <span className="text-[11px] italic text-slate-400">not set</span>;
  const d = daysUntil(complaint.final_response_due);
  const cls = d < 0 ? "bg-rose-100 text-rose-800 border-rose-300"
    : d <= 5 ? "bg-amber-100 text-amber-800 border-amber-300"
    : "bg-slate-100 text-slate-700 border-slate-300";
  return (
    <Badge variant="outline" className={`text-[10px] ${cls}`}>
      {ukDate(complaint.final_response_due)} · {d < 0 ? `${Math.abs(d)}d overdue` : `${d}d left`}
    </Badge>
  );
}

// ─── Case picker (search all cases) ─────────────────────────────────────────
function CasePicker({ value, onChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const search = (term) => {
    setQ(term);
    clearTimeout(timer.current);
    if (term.trim().length < 2) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      try { const r = await complaintCaseSearch(term.trim()); setResults(r.cases || []); }
      catch { setResults([]); }
    }, 300);
  };

  if (value) {
    return (
      <div className="flex items-center gap-1.5 h-9 border rounded px-2 bg-blue-50 border-blue-300" data-testid="complaint-case-picked">
        <span className="text-xs text-blue-900 truncate flex-1">
          {value.customer_name} {value.sf_number ? `· ${value.sf_number}` : ""}
        </span>
        <button type="button" onClick={() => onChange(null)} className="text-blue-400 hover:text-blue-800" data-testid="complaint-case-clear">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }
  return (
    <div className="relative">
      <Input value={q} onChange={(e) => { search(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search customer name or SF number…"
        className="h-9 text-sm" data-testid="complaint-case-search" />
      {open && results.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 border rounded bg-white shadow-lg divide-y max-h-44 overflow-y-auto">
          {results.map(c => (
            <button key={c.case_id} type="button"
              onMouseDown={(ev) => { ev.preventDefault(); onChange(c); setQ(""); setOpen(false); }}
              className="w-full text-left px-2 py-1.5 text-xs hover:bg-slate-50"
              data-testid={`complaint-case-opt-${c.case_id}`}>
              <span className="font-medium">{c.customer_name}</span>
              <span className="text-muted-foreground"> {c.sf_number ? `· ${c.sf_number}` : ""} {c.stage ? `· ${c.stage}` : ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Log complaint dialog ───────────────────────────────────────────────────
export function LogComplaintDialog({ open, onClose, onCreated, fixedCase = null }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [handlers, setHandlers] = useState([]);
  const [pickedCase, setPickedCase] = useState(fixedCase);
  const [dateReceived, setDateReceived] = useState(todayIso());
  const [topic, setTopic] = useState("");
  const [assigned, setAssigned] = useState("");
  const [details, setDetails] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPickedCase(fixedCase);
    setDateReceived(todayIso()); setTopic(""); setAssigned(""); setDetails("");
    getComplaintsSettings().then(setSettings).catch(() => {});
    getAgeingHandlers().then(r => setHandlers(r.handlers || [])).catch(() => {});
  }, [open, fixedCase]);

  const submit = async () => {
    if (!pickedCase) { toast.error("Link the complaint to a customer case"); return; }
    if (!topic) { toast.error("Pick a complaint topic"); return; }
    setSaving(true);
    try {
      const r = await createComplaint({
        case_id: pickedCase.case_id,
        date_received: dateReceived,
        topic, details, assigned_to: assigned,
        created_by: user?.name || "Ops",
      });
      toast.success("Complaint logged — acknowledgement action added to the Legal Calendar");
      onCreated?.(r.complaint);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log complaint");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" aria-describedby={undefined} data-testid="log-complaint-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Megaphone className="w-4 h-4 text-rose-600" /> Log a complaint
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-medium text-slate-600">Customer / case</label>
            <CasePicker value={pickedCase} onChange={setPickedCase} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-medium text-slate-600">Date received</label>
              <Input type="date" value={dateReceived} max={todayIso()}
                onChange={(e) => setDateReceived(e.target.value)}
                className="h-9 text-sm" data-testid="complaint-date-received" />
            </div>
            <div>
              <label className="text-[11px] font-medium text-slate-600">Topic</label>
              <Select value={topic} onValueChange={setTopic}>
                <SelectTrigger className="h-9 text-sm" data-testid="complaint-topic-select">
                  <SelectValue placeholder="Select topic…" />
                </SelectTrigger>
                <SelectContent>
                  {(settings?.topics || []).map(t => (
                    <SelectItem key={t} value={t} data-testid={`complaint-topic-${t}`}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-600">Assigned to</label>
            <Select value={assigned} onValueChange={setAssigned}>
              <SelectTrigger className="h-9 text-sm" data-testid="complaint-assigned-select">
                <SelectValue placeholder="Pick a portal user…" />
              </SelectTrigger>
              <SelectContent>
                {handlers.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-600">Complaint details</label>
            <Textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={4}
              placeholder="What is the complaint about?" className="text-sm" data-testid="complaint-details-input" />
          </div>
          <div className="text-[11px] text-slate-500 bg-slate-50 border rounded p-2">
            Logging adds a <b>“Complaint acknowledgement”</b> action to the Legal Calendar due in{" "}
            {settings?.ack_due_days ?? 2} days, and a “Complaint raised” note to the customer's timeline.
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={submit} disabled={saving} data-testid="complaint-submit-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
              Log complaint
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Acknowledgement email dialog ───────────────────────────────────────────
function AckEmailDialog({ complaint, settings, onClose, onSent }) {
  const { user } = useAuth();
  const fill = (tpl) => (tpl || "")
    .replaceAll("{customer_name}", complaint.customer_name || "Customer")
    .replaceAll("{date_received}", ukDate(complaint.date_received))
    .replaceAll("{topic}", complaint.topic || "your account")
    .replaceAll("{response_due}", ukDate(complaint.final_response_due))
    .replaceAll("{sf_number}", complaint.sf_number || "");
  const [to, setTo] = useState(complaint.customer_email || "");
  const [subject, setSubject] = useState(`We have received your complaint — ${complaint.customer_name || ""}`);
  const [body, setBody] = useState(fill(settings?.ack_template));
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      await sendComplaintAck(complaint.complaint_id, { to, subject, body, actor: user?.name || "Ops" });
      toast.success(`Acknowledgement sent to ${to}`);
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" aria-describedby={undefined} data-testid="complaint-ack-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Mail className="w-4 h-4 text-blue-600" /> Send acknowledgement email
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2.5">
          <div>
            <label className="text-[11px] font-medium text-slate-600">To</label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="customer@company.co.uk"
              className="h-9 text-sm" data-testid="complaint-ack-to" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-600">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="h-9 text-sm" data-testid="complaint-ack-subject" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-600">Body (template pre-filled — edit as needed)</label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={11} className="text-sm" data-testid="complaint-ack-body" />
          </div>
          <div className="flex items-center justify-between">
            <div className="text-[11px] text-slate-500">Sends from the legal mailbox · logged to the complaint's comms</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
              <Button size="sm" onClick={send} disabled={sending || !to.includes("@")} data-testid="complaint-ack-send-btn">
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                Send
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Complaint detail dialog ────────────────────────────────────────────────
export function ComplaintDetailDialog({ complaint: initial, onClose, onChanged }) {
  const { user } = useAuth();
  const [c, setC] = useState(initial);
  const [settings, setSettings] = useState(null);
  const [handlers, setHandlers] = useState([]);
  const [due, setDue] = useState(initial.final_response_due || "");
  const [outcome, setOutcome] = useState(initial.outcome || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [showAckEmail, setShowAckEmail] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const doDelete = async () => {
    setBusy(true);
    try {
      await deleteComplaint(c.complaint_id);
      toast.success("Complaint deleted — calendar events removed");
      onChanged?.(null);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    getComplaintsSettings().then(setSettings).catch(() => {});
    getAgeingHandlers().then(r => setHandlers(r.handlers || [])).catch(() => {});
  }, []);

  const maxDue = (() => {
    if (!settings || !c.date_received) return undefined;
    const d = new Date(c.date_received + "T00:00:00");
    d.setDate(d.getDate() + (settings.max_response_days || 30));
    return d.toISOString().slice(0, 10);
  })();

  const patch = async (payload, successMsg) => {
    setBusy(true);
    try {
      const r = await updateComplaint(c.complaint_id, { ...payload, actor: user?.name || "Ops" });
      setC(r.complaint);
      onChanged?.(r.complaint);
      if (successMsg) toast.success(successMsg);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
      return false;
    } finally { setBusy(false); }
  };

  const acknowledge = async () => {
    if (!due) { toast.error("Set the final response due date first"); return; }
    await patch({ final_response_due: due, status: "acknowledged" },
      "Acknowledged — response-due action added to the Legal Calendar");
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      const r = await addComplaintComm(c.complaint_id, { text: note.trim(), author: user?.name || "Ops" });
      setC(prev => ({ ...prev, comms: [...(prev.comms || []), r.entry] }));
      setNote("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add note"); }
  };

  const isClosed = ["responded", "closed"].includes(c.status);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="complaint-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Megaphone className="w-4 h-4 text-rose-600" />
            {c.customer_name} {c.sf_number ? <span className="text-slate-400 font-normal">· {c.sf_number}</span> : null}
            <StatusBadge status={c.status} />
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3 text-sm">
          <div>
            <div className="text-[11px] text-slate-500">Received</div>
            <div className="font-medium">{ukDate(c.date_received)}</div>
          </div>
          <div>
            <div className="text-[11px] text-slate-500">Topic</div>
            <div className="font-medium">{c.topic}</div>
          </div>
          <div>
            <div className="text-[11px] text-slate-500">Assigned to</div>
            <Select value={c.assigned_to || ""} disabled={isClosed}
              onValueChange={(v) => patch({ assigned_to: v }, "Reassigned")}>
              <SelectTrigger className="h-7 text-xs" data-testid="complaint-detail-assigned">
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                {handlers.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {c.details ? (
          <div className="text-xs bg-slate-50 border rounded p-2 whitespace-pre-wrap">{c.details}</div>
        ) : null}

        {/* Deadline + acknowledge */}
        {!isClosed && (
          <div className="border rounded-lg p-3 space-y-2 bg-amber-50/50 border-amber-200">
            <div className="text-xs font-semibold text-amber-900 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Final response deadline
              {settings && <span className="font-normal text-amber-700">(max {settings.max_response_days} days from received{maxDue ? ` — latest ${ukDate(maxDue)}` : ""})</span>}
            </div>
            <div className="flex items-center gap-2">
              <Input type="date" value={due} min={c.date_received} max={maxDue}
                onChange={(e) => setDue(e.target.value)}
                className="h-8 text-xs w-40" data-testid="complaint-due-input" />
              {c.status === "open" ? (
                <Button size="sm" className="h-8 text-xs" onClick={acknowledge} disabled={busy || !due} data-testid="complaint-acknowledge-btn">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Acknowledge & set deadline
                </Button>
              ) : (
                <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy || !due || due === c.final_response_due}
                  onClick={() => patch({ final_response_due: due }, "Deadline updated — calendar event moved")}
                  data-testid="complaint-due-save-btn">
                  Update deadline
                </Button>
              )}
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setShowAckEmail(true)}
                disabled={!c.final_response_due} data-testid="complaint-ack-email-btn">
                <Mail className="w-3.5 h-3.5 mr-1" /> Send acknowledgement email
              </Button>
            </div>
            {!c.final_response_due && (
              <div className="text-[11px] text-amber-700">Set and save the deadline first — the acknowledgement email quotes it to the customer.</div>
            )}
          </div>
        )}

        {/* Comms log */}
        <div className="space-y-1.5">
          <div className="text-xs font-semibold text-slate-700">Comms log ({(c.comms || []).length})</div>
          <div className="max-h-40 overflow-y-auto space-y-1" data-testid="complaint-comms-list">
            {(c.comms || []).length === 0 && <div className="text-[11px] italic text-slate-400">No comms logged yet</div>}
            {[...(c.comms || [])].reverse().map(entry => (
              <div key={entry.id} className="text-xs border rounded p-1.5 bg-white">
                <span className={`font-medium ${entry.kind === "email_out" ? "text-blue-700" : "text-slate-700"}`}>
                  {entry.kind === "email_out" ? "📧 " : ""}{entry.author}
                </span>
                <span className="text-slate-400"> · {ukDate(entry.at)}</span>
                <div className="text-slate-600 whitespace-pre-wrap">{entry.text}</div>
              </div>
            ))}
          </div>
          {!isClosed && (
            <div className="flex gap-2">
              <Input value={note} onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addNote()}
                placeholder="Log a call, email or update…" className="h-8 text-xs" data-testid="complaint-comm-input" />
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={addNote} data-testid="complaint-comm-add-btn">Add</Button>
            </div>
          )}
        </div>

        {/* Outcome / close */}
        <div className="border rounded-lg p-3 space-y-2">
          <div className="text-xs font-semibold text-slate-700">Outcome / final decision</div>
          {isClosed ? (
            <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2 whitespace-pre-wrap" data-testid="complaint-outcome-display">
              {c.outcome}
              <div className="text-[10px] text-emerald-700 mt-1">Responded {ukDate(c.responded_at)}</div>
            </div>
          ) : (
            <>
              <Textarea value={outcome} onChange={(e) => setOutcome(e.target.value)} rows={3}
                placeholder="Summarise the final decision / response given to the customer…"
                className="text-xs" data-testid="complaint-outcome-input" />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy || !outcome.trim()}
                  onClick={() => patch({ outcome, status: "responded" }, "Marked responded — timeline updated")}
                  data-testid="complaint-responded-btn">
                  Mark responded
                </Button>
                <Button size="sm" className="h-8 text-xs" disabled={busy || !outcome.trim()}
                  onClick={() => patch({ outcome, status: "closed" }, "Complaint closed")}
                  data-testid="complaint-close-btn">
                  Close complaint
                </Button>
              </div>
            </>
          )}
        </div>

        {showAckEmail && (
          <AckEmailDialog complaint={c} settings={settings}
            onClose={() => setShowAckEmail(false)}
            onSent={() => {
              updateComplaint(c.complaint_id, { actor: user?.name || "Ops" }).catch(() => {});
              setC(prev => ({ ...prev }));
            }} />
        )}

        <div className="flex items-center justify-start pt-1 border-t">
          {confirmDelete ? (
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-rose-700">Delete this complaint and its calendar events?</span>
              <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={doDelete} disabled={busy} data-testid="complaint-delete-confirm-btn">
                Yes, delete
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setConfirmDelete(false)}>Cancel</Button>
            </div>
          ) : (
            <button type="button" onClick={() => setConfirmDelete(true)}
              className="text-[11px] text-slate-400 hover:text-rose-600 flex items-center gap-1"
              data-testid="complaint-delete-btn">
              <Trash2 className="w-3 h-3" /> Delete complaint
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Complaints table (shared) ──────────────────────────────────────────────
function ComplaintsTable({ rows, onOpen }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b">
            <th className="py-2 pr-3">Customer</th>
            <th className="py-2 pr-3">Topic</th>
            <th className="py-2 pr-3">Received</th>
            <th className="py-2 pr-3">Assigned</th>
            <th className="py-2 pr-3">Response due</th>
            <th className="py-2 pr-3">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(c => (
            <tr key={c.complaint_id} className="border-b hover:bg-slate-50 cursor-pointer"
              onClick={() => onOpen(c)} data-testid={`complaint-row-${c.complaint_id}`}>
              <td className="py-2 pr-3">
                <div className="font-medium text-slate-800">{c.customer_name}</div>
                <div className="text-[11px] text-slate-400">{c.sf_number}</div>
              </td>
              <td className="py-2 pr-3 text-xs">{c.topic}</td>
              <td className="py-2 pr-3 text-xs">{ukDate(c.date_received)}</td>
              <td className="py-2 pr-3 text-xs">{c.assigned_to || <span className="italic text-slate-400">unassigned</span>}</td>
              <td className="py-2 pr-3"><DueBadge complaint={c} /></td>
              <td className="py-2 pr-3"><StatusBadge status={c.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && (
        <div className="text-center text-sm text-slate-400 py-10" data-testid="complaints-empty">No complaints match</div>
      )}
    </div>
  );
}

// ─── Customer-file panel (Complaints tab inside the case file) ─────────────
export function CaseComplaintsPanel({ caseRow }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLog, setShowLog] = useState(false);
  const [openC, setOpenC] = useState(null);

  const load = useCallback(async () => {
    try { const r = await complaintsForCase(caseRow.case_id); setRows(r.complaints || []); }
    catch { /* noop */ }
    finally { setLoading(false); }
  }, [caseRow.case_id]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-rose-600" /> Complaints ({rows.length})
          </div>
          <Button size="sm" className="h-8 text-xs" onClick={() => setShowLog(true)} data-testid="case-log-complaint-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Log complaint
          </Button>
        </div>
        {loading ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
          : <ComplaintsTable rows={rows} onOpen={setOpenC} />}
        <LogComplaintDialog open={showLog} onClose={() => setShowLog(false)}
          fixedCase={{ case_id: caseRow.case_id, customer_name: caseRow.customer_name, sf_number: caseRow.loan_application_id }}
          onCreated={() => load()} />
        {openC && <ComplaintDetailDialog complaint={openC} onClose={() => setOpenC(null)} onChanged={() => load()} />}
      </CardContent>
    </Card>
  );
}

// ─── Register page ──────────────────────────────────────────────────────────
export default function ComplaintsRegister() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [showLog, setShowLog] = useState(false);
  const [openC, setOpenC] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listComplaints({ status: statusFilter || undefined, q: q || undefined });
      setRows(r.complaints || []);
      setCounts(r.counts || {});
    } catch (e) {
      toast.error("Failed to load complaints");
    } finally { setLoading(false); }
  }, [statusFilter, q]);
  useEffect(() => { load(); }, [load]);

  const openCount = (counts.open || 0) + (counts.acknowledged || 0);

  return (
    <div className="space-y-4" data-testid="complaints-register">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-rose-600" /> Complaints
          </h1>
          <p className="text-xs text-slate-500">
            {openCount} open · acknowledgements auto-diarised to the <Link to="/legal/calendar" className="underline">Legal Calendar</Link>
          </p>
        </div>
        <Button size="sm" onClick={() => setShowLog(true)} data-testid="log-complaint-btn">
          <Plus className="w-4 h-4 mr-1" /> Log complaint
        </Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {["", "open", "acknowledged", "responded", "closed"].map(s => (
          <button key={s || "all"} onClick={() => setStatusFilter(s)}
            className={`px-2.5 py-1 rounded text-xs border transition ${statusFilter === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            data-testid={`complaints-filter-${s || "all"}`}>
            {s ? `${STATUS_META[s].label} (${counts[s] || 0})` : `All (${Object.values(counts).reduce((a, b) => a + b, 0)})`}
          </button>
        ))}
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer / SF / topic…"
          className="h-8 text-xs w-56 ml-auto" data-testid="complaints-search" />
      </div>

      <Card>
        <CardContent className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : (
            <ComplaintsTable rows={rows} onOpen={setOpenC} />
          )}
        </CardContent>
      </Card>

      <LogComplaintDialog open={showLog} onClose={() => setShowLog(false)} onCreated={() => load()} />
      {openC && <ComplaintDetailDialog complaint={openC} onClose={() => setOpenC(null)} onChanged={() => load()} />}
    </div>
  );
}
